import { BadgeCheck, CircleDashed, FlaskConical, Sigma } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { defineMessages, type PlainMessage } from '../../i18n/define';
import { useT } from '../../i18n/hooks';
import { Icon } from './Icon';

/** Класс данных. Кандидат никогда не «повышается» до факта визуально. */
export type EvidenceClass = 'fact' | 'derived' | 'candidate' | 'demo';

const m = defineMessages({
  factLabel: { id: 'common.ui.evidence.fact.label', defaultMessage: 'Факт', description: 'Бейдж класса данных: значение взято из источника; коротко' },
  factHint: { id: 'common.ui.evidence.fact.hint', defaultMessage: 'значение из источника', description: 'Всплывающая подсказка бейджа «Факт»' },
  derivedLabel: { id: 'common.ui.evidence.derived.label', defaultMessage: 'Производное', description: 'Бейдж класса данных: значение вычислено из источников; коротко' },
  derivedHint: { id: 'common.ui.evidence.derived.hint', defaultMessage: 'вычислено из источников', description: 'Всплывающая подсказка бейджа «Производное»' },
  candidateLabel: { id: 'common.ui.evidence.candidate.label', defaultMessage: 'Кандидат', description: 'Бейдж класса данных: сопоставление не подтверждено; коротко' },
  candidateHint: { id: 'common.ui.evidence.candidate.hint', defaultMessage: 'сопоставление не подтверждено', description: 'Всплывающая подсказка бейджа «Кандидат»' },
  demoLabel: { id: 'common.ui.evidence.demo.label', defaultMessage: 'Демо', description: 'Бейдж класса данных: демонстрационные данные прототипа; коротко' },
  demoHint: { id: 'common.ui.evidence.demo.hint', defaultMessage: 'синтетика прототипа, не факт', description: 'Всплывающая подсказка бейджа «Демо»' },
});

const SPEC: Record<EvidenceClass, { label: PlainMessage; icon: LucideIcon; hint: PlainMessage }> = {
  fact: { label: m.factLabel, icon: BadgeCheck, hint: m.factHint },
  derived: { label: m.derivedLabel, icon: Sigma, hint: m.derivedHint },
  candidate: { label: m.candidateLabel, icon: CircleDashed, hint: m.candidateHint },
  demo: { label: m.demoLabel, icon: FlaskConical, hint: m.demoHint },
};

export interface EvidenceMarkProps {
  kind: EvidenceClass;
  /** Уточнение вместо стандартной подписи класса. */
  label?: string;
}

/**
 * Класс данных читается иконкой и подписью, а не цветом: в оттенках серого
 * значение не теряется. У каждого класса своя обводка (сплошная/пунктир/
 * штрих) и своя иконка.
 */
export function EvidenceMark({ kind, label }: EvidenceMarkProps) {
  const t = useT();
  const spec = SPEC[kind];
  return (
    <span className={`evidence evidence--${kind}`} title={t(spec.hint)}>
      <Icon icon={spec.icon} size={16} />
      {label ?? t(spec.label)}
    </span>
  );
}
